import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };
const WIDTH = 600;
const HEIGHT = 400;

export interface BurndownChartProps {
  readonly data: Array<{ dimensionValue: string; measureValue: number }>;
  readonly dimensionLabel: string;
  readonly measureLabel: string;
  readonly onSegmentClick?: (dimensionValue: string) => void;
}

/**
 * Burndown chart with Ideal (dashed gray) and Actual (solid primary) lines.
 * Data format: dimensionValue = date, measureValue = remaining.
 * The ideal line is computed as a linear interpolation from first value to 0.
 */
export function BurndownChart({ data, dimensionLabel, measureLabel, onSegmentClick }: BurndownChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handlePointInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({
        dimensionValue,
        measureValue,
        dimensionLabel,
        measureLabel,
        x: targetRect.left - rect.left + targetRect.width / 2,
        y: targetRect.top - rect.top,
      });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Compute ideal line from first data point to zero
  const idealData = React.useMemo(() => {
    if (data.length === 0) return [];
    const startValue = data[0]!.measureValue;
    const totalPoints = data.length - 1;
    return data.map((d, i) => ({
      dimensionValue: d.dimensionValue,
      measureValue: totalPoints > 0
        ? Math.max(0, startValue * (1 - i / totalPoints))
        : 0,
    }));
  }, [data]);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const maxValue = Math.max(
      d3.max(data, (d) => d.measureValue) ?? 0,
      d3.max(idealData, (d) => d.measureValue) ?? 0,
    );

    const xScale = d3
      .scalePoint()
      .domain(data.map((d) => d.dimensionValue))
      .range([0, innerWidth])
      .padding(0.1);

    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .nice()
      .range([innerHeight, 0]);

    // Gridlines
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale).tickSize(-innerWidth).tickFormat(() => ''))
      .selectAll('line')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-dasharray', '2,2');
    g.select('.grid .domain').remove();

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).tickValues(
        data.length > 14
          ? data.filter((_, i) => i % Math.ceil(data.length / 7) === 0).map((d) => d.dimensionValue)
          : data.map((d) => d.dimensionValue),
      ))
      .selectAll('text')
      .attr('transform', 'rotate(-30)')
      .style('text-anchor', 'end')
      .attr('font-size', '11px');

    // Y axis
    g.append('g').call(d3.axisLeft(yScale)).attr('font-size', '12px');

    // Axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + MARGIN.bottom - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(dimensionLabel);

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -MARGIN.left + 15)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('fill', 'currentColor')
      .text(measureLabel);

    // Ideal line (dashed, gray)
    const idealLine = d3
      .line<{ dimensionValue: string; measureValue: number }>()
      .x((d) => xScale(d.dimensionValue) ?? 0)
      .y((d) => yScale(d.measureValue));

    g.append('path')
      .datum(idealData)
      .attr('fill', 'none')
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,4')
      .attr('d', idealLine);

    // Actual line (solid, primary color)
    const actualLine = d3
      .line<{ dimensionValue: string; measureValue: number }>()
      .x((d) => xScale(d.dimensionValue) ?? 0)
      .y((d) => yScale(d.measureValue));

    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5)
      .attr('d', actualLine);

    // Data point markers on actual line
    data.forEach((d) => {
      g.append('circle')
        .attr('cx', xScale(d.dimensionValue) ?? 0)
        .attr('cy', yScale(d.measureValue))
        .attr('r', 4)
        .attr('fill', '#2563eb')
        .attr('stroke', 'white')
        .attr('stroke-width', 1.5);
    });
  }, [data, idealData, dimensionLabel, measureLabel]);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  const innerWidth = WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const maxValue = Math.max(
    d3.max(data, (d) => d.measureValue) ?? 0,
    d3.max(idealData, (d) => d.measureValue) ?? 0,
  );

  const xScale = d3.scalePoint().domain(data.map((d) => d.dimensionValue)).range([0, innerWidth]).padding(0.1);
  const yScale = d3.scaleLinear().domain([0, maxValue]).nice().range([innerHeight, 0]);
  const POINT_SIZE = 24;

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Burndown chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full max-w-[600px]" aria-hidden="true">
        <ChartPatternDefs />
      </svg>
      {/* Legend */}
      <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 bg-blue-600" />Actual
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 border-t-2 border-dashed border-gray-400" />Ideal
        </span>
      </div>
      {/* Keyboard-navigable overlays */}
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {data.map((d) => {
          const cx = (MARGIN.left + (xScale(d.dimensionValue) ?? 0)) / WIDTH * 100;
          const cy = (MARGIN.top + yScale(d.measureValue)) / HEIGHT * 100;
          const pw = POINT_SIZE / WIDTH * 100;
          const ph = POINT_SIZE / HEIGHT * 100;
          return (
            <button
              key={d.dimensionValue}
              type="button"
              role="listitem"
              className="absolute rounded-full border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              style={{ left: `${cx - pw / 2}%`, top: `${cy - ph / 2}%`, width: `${pw}%`, height: `${ph}%` }}
              tabIndex={0}
              aria-label={`${dimensionLabel}: ${d.dimensionValue}, ${measureLabel}: ${d.measureValue}`}
              onMouseEnter={(e) => handlePointInteraction(d.dimensionValue, d.measureValue, e)}
              onFocus={(e) => handlePointInteraction(d.dimensionValue, d.measureValue, e)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
              onClick={() => onSegmentClick?.(d.dimensionValue)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSegmentClick?.(d.dimensionValue); } }}
            />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
